// ✅ src/pages/packages-management/components/TestParameterForm.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Plus,
  Trash2,
  Bold,
  X,
  Edit2,
  ArrowLeft,
  GripVertical,
  Save,
  Search,
  ChevronDown,
} from "lucide-react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import Breadcrumb from "../../../components/ui/Breadcrumb";
import Sidebar from "../../../components/ui/Sidebar";
import Header from "../../../components/ui/Header";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import axiosInstance from "../../../utils/axiosInstance";
import TinyMCEFullEditor from "../../../components/editors/TinyMCEFullEditor";

// ✅ IMPORTANT: HtmlPreview.jsx (NOT HtmlPreview.js)
import HtmlPreview from "../../../utils/HtmlPreview";

// ✅ Reusable modal
import ReportItemModal, {
  ITEM_TYPES,
  ITEM_LABELS,
  GENDER,
} from "./ReportItemModal";
import { useAuth } from "../../../context/AuthContext";

// ---------- SearchableSelect ----------
const ALL_REFERENCE_RANGES = [
  "Any","Premature Born","2 weeks","Post Menopausal","Pregnant Female",
  "Non Pregnant Female","Adolescents (13-20yrs)","Upto 45 years",
  "Upto 6 Months","0 to 4 Days","upto 10 years","Newborn (0 day to 1 day)",
  "Upto 12 years","Upto 60 years","0 to 120 years","Upto 13 years",
  "Upto 75 years","6 months","upto 14 years","upto 5 years","12 months",
  "upto 1 year","Upto 20 years","0 to 2 Days","upto 2 years",
  "upto 3 months","0 to 20 years","0 to 1 year","0 to 59 year",
  "0 year 3 year","Upto 4 years","Upto 50 years","upto 6 years",
  "Upto 65 years","Newborn (0 day to 3 day)","0 to 30 Days",
  "0 days to 15 days","Upto 18 years","Newborn (1 day - 6 days)",
  "Newborn (1 day - 4 days)","1 day to 18 years","2 Days to 4 Days",
  "1 day to 20 weeks","2 days to 7 days","2 days to 4 years",
  "3 days - 30 days","3 days to 6 days","3 Days","3 days to 1 year",
  "4 days to 14 years","4 days to 8 days","2 days to 5 days",
  "6 days to 12 days","6 days to 3 months","6 Days to 90 Days",
  "Newborn (7 days - 1 month)","Newborn (8 days to 1 month)",
  "8 days to 1 year","8 days to 15 days","8 days to 14 days",
  "8 days to 5 years","16 days to 31 days","2 to 30 Days",
  "28 days to 1 year","1 month to 1 year","1 month to 10 month",
  "1 month to 6 months","61 days to 181 days","3 months to 1 year",
  "3 months to 5 months","3 month to 6 year","3 months to 9 months",
  "3 months to 6 months","4 months to 6 months","6 months and above",
  "7 months to 24 months","7 months to 1 year","9 months to 1 year",
  "9 months to 2 years","1 year to 5 years","1 year to 12 years",
  "1 year to 4 years","1 year to 6 years","1 year to 3 years",
  "2 years to 12 Years","2 year to 4 year","Above 2 years",
  "2 year to 3 year","3 years to 7 Years","3 year to 4 years",
  "3 years to 18 years","4 years to 6 years","4 years to 13 years",
  "4 years to 7 years","4 years to 15 years","4 year to 11 years",
  "4 years to 10 years","6 years to 13 years","6 Year to 11 Years",
  "7 years to 13 years","7 years to 12 years","7 years to 11 years",
  "10 years to 59 years","10 years to 50 years","10 years to 18 years",
  "10 years to 15 years","10 years to 11 years","10 years to 13 years",
  "12 years to 21 years","12 years to 18 years","Above 12 years",
  "12 years to 60 years","12 years to 25 years","12 year to 15 years",
  "14 years to 20 years","above 14 year","14 years to 17 years",
  "above 15 years","15 years to 16 years","15 years to 19 years",
  "18 Years to 55 Years","18 years to 50 years","18 Years to 25 Years",
  "above 18 years","18 years to 24 years","19Year to 44Year",
  "21 years to 110 years","21 years - 54 years","21 years to 40 years",
  "25 years to 30 years","25 years","25 years to 29 years",
  "35 years to 39 years","36 Years to 40 Years","40 Years to 50 Years",
  "40 to 49 years","40 years to 70 years","50 years to 54 years",
  "50 years to 60 years","51 YEARS TO 85 YEARS","50 to 59 years",
  "above 50 years","above 51 years","60 years to 64 years",
  "above 65 years","65 years to 69 years","above 69 years",
  "above 70 years","80 years to 84 years","above 90 years",
  "85 years to 89 years","86 years to 110 years","above 88 years",
  "19 Years to 120 Years",
];

function SearchableRangeSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const filtered = query.trim()
    ? ALL_REFERENCE_RANGES.filter((r) =>
        r.toLowerCase().includes(query.toLowerCase())
      )
    : ALL_REFERENCE_RANGES;

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // focus search when opened
  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  const select = (label) => {
    onChange(label);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
      >
        <span className={`truncate ${!value || value === "Any" ? "text-gray-400" : "text-gray-800"}`}>
          {value || "Any"}
        </span>
        <ChevronDown size={14} className={`ml-1 text-gray-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[220px] bg-white border border-gray-200 rounded-md shadow-lg">
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 border border-gray-200 rounded">
              <Search size={12} className="text-gray-400 flex-shrink-0" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search range..."
                className="flex-1 text-xs bg-transparent outline-none text-gray-700 placeholder-gray-400"
                onKeyDown={(e) => {
                  if (e.key === "Escape") { setOpen(false); setQuery(""); }
                  if (e.key === "Enter" && filtered.length === 1) select(filtered[0]);
                }}
              />
              {query && (
                <button type="button" onClick={() => setQuery("")} className="text-gray-400 hover:text-gray-600">
                  <X size={11} />
                </button>
              )}
            </div>
          </div>

          {/* Options list */}
          <div ref={listRef} className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400">No match found</div>
            ) : (
              filtered.map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => select(label)}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 hover:text-indigo-700 transition-colors ${
                    label === value ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-700"
                  }`}
                >
                  {label}
                </button>
              ))
            )}
          </div>

          {/* Count */}
          <div className="px-3 py-1.5 border-t border-gray-100 text-[10px] text-gray-400">
            {filtered.length} of {ALL_REFERENCE_RANGES.length} ranges
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- helpers ----------
const stripHtml = (html) => {
  try {
    let s = String(html || "");
    s = s
      .replace(/<\/(td|th)>/gi, " ")
      .replace(/<\/tr>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n");

    const text = s
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
      .replace(/<\/?[^>]+(>|$)/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\u00A0/g, " ")
      .replace(/[ \t]{2,}/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

    return text;
  } catch (e) {
    console.error(e);
    return "";
  }
};

const isEmptyHtml = (html) => {
  try {
    return stripHtml(html).length === 0;
  } catch (e) {
    console.error(e);
    return true;
  }
};

const getRowBg = (type) => {
  switch (type) {
    case ITEM_TYPES.HEADING:
      return "bg-amber-50 hover:bg-amber-100/60";
    case ITEM_TYPES.NOTES:
      return "bg-purple-50 hover:bg-purple-100/60";
    case ITEM_TYPES.RICH_TEXT:
      return "bg-indigo-50 hover:bg-indigo-100/60";
    case ITEM_TYPES.FREE_TEXT:
      return "bg-rose-50 hover:bg-rose-100/60";
    case ITEM_TYPES.PARAMETER:
      return "bg-emerald-50 hover:bg-emerald-100/60";
    default:
      return "bg-white hover:bg-gray-50";
  }
};

// ---------------- Sortable Item ----------------
const SortableItem = ({
  item,
  onEditItem,
  onDeleteItem,
  onEditParam,
  onDeleteParam,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 50 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`group relative border-b border-gray-200 transition-colors cursor-grab active:cursor-grabbing ${getRowBg(
        item.type,
      )} ${isDragging ? "shadow-lg ring-2 ring-indigo-300 scale-[1.01] z-50" : ""}`}>
      <div className="flex items-start gap-3 p-4">
        <div className="mt-1.5 text-gray-300 group-hover:text-gray-500">
          <GripVertical className="w-4 h-4" />
        </div>

        <div className="text-xs bg-white/70 text-gray-800 px-2 py-1 rounded font-medium border border-gray-200">
          #{item.sortOrder}
        </div>

        <div className="flex-1 min-w-0">
          {item.type === ITEM_TYPES.HEADING && (
            <div className="text-base font-semibold text-gray-900">
              {item.title || "Untitled Heading"}
            </div>
          )}

          {item.type === ITEM_TYPES.NOTES && (
            <div className="space-y-1">
              <HtmlPreview html={item.html || item.text || ""} />
            </div>
          )}

          {item.type === ITEM_TYPES.RICH_TEXT && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-indigo-800">
                {item.title || "Untitled Other Text"}
              </span>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded-full border border-indigo-200">
                ✏️ Filled at result time
              </span>
            </div>
          )}

          {item.type === ITEM_TYPES.FREE_TEXT && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-rose-800">
                {item.title || "Untitled Free Text"}
              </span>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-rose-100 text-rose-600 rounded-full border border-rose-200">
                ✏️ Filled at result time
              </span>
            </div>
          )}

          {item.type === ITEM_TYPES.PARAMETER && item.parameter && (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-base font-medium text-gray-900">
                  {item.parameter.name}
                </span>
                {item.parameter.unit && (
                  <span className="text-xs bg-white/70 text-gray-700 px-2 py-0.5 rounded-full border border-gray-200">
                    {item.parameter.unit}
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
                  {item.parameter.type}
                </span>

                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${item.parameter.isActive
                      ? "bg-green-50 text-green-700"
                      : "bg-gray-100 text-gray-600"
                    }`}>
                  {item.parameter.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          )}
        </div>

        <div
          className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}>
          {item.type !== ITEM_TYPES.PARAMETER ? (
            <>
              <button
                onClick={() => onEditItem(item)}
                className="p-1.5 text-gray-600 hover:text-blue-700 hover:bg-white/70 rounded-md transition-colors"
                title="Edit"
                type="button">
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDeleteItem(item.id)}
                className="p-1.5 text-gray-600 hover:text-red-700 hover:bg-white/70 rounded-md transition-colors"
                title="Delete"
                type="button">
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onEditParam(item.parameter)}
                className="p-1.5 text-gray-600 hover:text-blue-700 hover:bg-white/70 rounded-md transition-colors"
                title="Edit Parameter"
                type="button">
                <Edit2 className="w-4 h-4" />
              </button>

              <button
                onClick={() => onDeleteParam(item.parameter.id)}
                className="p-1.5 text-gray-600 hover:text-red-700 hover:bg-white/70 rounded-md transition-colors"
                title="Delete Parameter"
                type="button">
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default function TestParameterForm() {
  try {
    const { testId } = useParams();
    const navigate = useNavigate();
    const { state } = useLocation();
    const testName = state?.testName;

    const [sidebarCollapsed] = useState(false);
    const [sidebarOpen] = useState(false);

    const [activeTab, setActiveTab] = useState("parameters");

    // ---- parameter modal ----
    const [isOpen, setIsOpen] = useState(false);
    const [editingParameter, setEditingParameter] = useState(null);

    // parameter form
    const [name, setName] = useState("");
    const [method, setMethod] = useState("");
    const [type, setType] = useState("");
    const [unit, setUnit] = useState("");
    const [order, setOrder] = useState("");
    const [notes, setNotes] = useState("");
    const [isActive, setIsActive] = useState(true);
    const defaultOpts = [
      { label: "Positive", isBold: true },
      { label: "Negative", isBold: true },
      { label: "Nil", isBold: true },
    ];
    const [options, setOptions] = useState(defaultOpts);

    const blankRange = () => ({
      lowerLimit: "",
      upperLimit: "",
      criticalLow: "",
      criticalHigh: "",
      referenceRange: "Any",
      gender: "Both",
      normalValueHtml: "",
      specialConditionHtml: "",
    });
    const [ranges, setRanges] = useState([blankRange()]);

    // ---- parameter list ----
    const [testParameters, setTestParameters] = useState([]);
    const [loading, setLoading] = useState(false);

    // ---- report items ----
    const [items, setItems] = useState([]);
    const [itemsLoading, setItemsLoading] = useState(false);

    const [itemModalOpen, setItemModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itemType, setItemType] = useState(ITEM_TYPES.HEADING);
    const [itemTitle, setItemTitle] = useState("");
    const [itemHtml, setItemHtml] = useState("");
    const [itemGender, setItemGender] = useState(GENDER.BOTH); // ✅ fixed
    const [itemSortOrder, setItemSortOrder] = useState("");

    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );

    useEffect(() => {
      try {
        if (!testId) return;
        (async () => {
          try {
            await Promise.all([fetchItems(), fetchParameters()]);
          } catch (e) {
            console.error(e);
          }
        })();
      } catch (e) {
        console.error(e);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testId]);

    // ---------------- API ----------------
    const fetchParameters = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `/api/parameters/list1/${testId}`,
        );
        setTestParameters(response.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch parameters", err);
        alert("Error loading parameters");
      } finally {
        setLoading(false);
      }
    };

    const fetchItems = async () => {
      setItemsLoading(true);
      try {
        const res = await axiosInstance.get(`/api/report-items/list/${testId}`);
        setItems(res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch report items", err);
        setItems([]);
      } finally {
        setItemsLoading(false);
      }
    };

    // ---------------- DnD reorder ----------------
    const handleDragEnd = async (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      try {
        const oldIndex = items.findIndex((it) => it.id === active.id);
        const newIndex = items.findIndex((it) => it.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        const updatedItems = newItems.map((it, idx) => ({
          ...it,
          sortOrder: idx + 1,
        }));

        setItems(updatedItems);

        await axiosInstance.put(`/api/report-items/reorder/${testId}`, {
          items: updatedItems.map((it) => ({
            id: it.id,
            sortOrder: it.sortOrder,
          })),
        });
      } catch (err) {
        console.error(err);
        alert("Failed to reorder items");
        await fetchItems();
      }
    };

    // ---------------- Items CRUD ----------------
    const openNewItem = (t) => {
      try {
        setEditingItem(null);
        setItemType(t);
        setItemTitle("");
        setItemHtml("");
        setItemGender(GENDER.BOTH);
        setItemSortOrder((items.length + 1).toString());
        setItemModalOpen(true);
      } catch (e) {
        console.error(e);
      }
    };

    const editItem = (it) => {
      try {
        setEditingItem(it);
        setItemType(it.type);
        setItemTitle(it.title || "");
        setItemHtml(it.html || it.text || "");
        setItemSortOrder(String(it.sortOrder ?? ""));
        setItemGender(it.gender || GENDER.BOTH);
        setItemModalOpen(true);
      } catch (e) {
        console.error(e);
      }
    };

    const deleteItem = async (itemId) => {
      try {
        if (!window.confirm("Delete this item?")) return;
        await axiosInstance.delete(`/api/report-items/${itemId}`);
        await fetchItems();
      } catch (err) {
        console.error(err);
        alert("Failed to delete item");
      }
    };

    const saveItem = async () => {
      try {
        if (itemType === ITEM_TYPES.HEADING && !itemTitle.trim()) {
          return alert("Heading title is required");
        }
        if (itemType === ITEM_TYPES.NOTES && isEmptyHtml(itemHtml)) {
          return alert("Notes content is required");
        }
        if (itemType === ITEM_TYPES.RICH_TEXT && !String(itemTitle || "").trim()) {
          return alert("Other Text title is required");
        }

        const payload = {
          type: itemType,
          sortOrder: itemSortOrder ? Number(itemSortOrder) : items.length + 1,
          title:
            itemType === ITEM_TYPES.HEADING
              ? itemTitle.trim() || null
              : itemType === ITEM_TYPES.RICH_TEXT
                ? (itemTitle?.trim() || null)
                : null,
          text: null,
          gender: itemGender, // ✅ fixed
          html:
            itemType === ITEM_TYPES.NOTES ||
            itemType === ITEM_TYPES.FREE_TEXT
              ? itemHtml
              : null,
          parameterId: null,
          createdById: user?.id,
        };

        if (editingItem?.id) {
          await axiosInstance.put(
            `/api/report-items/${editingItem.id}`,
            payload,
          );
        } else {
          await axiosInstance.post(`/api/report-items/${testId}`, payload);
        }

        setItemModalOpen(false);
        setEditingItem(null);
        await fetchItems();
      } catch (err) {
        console.error(err);
        alert(err?.response?.data?.error || "Failed to save item");
      }
    };

    // ---------------- Parameter helpers ----------------
    const handleTypeChange = (newType) => {
      try {
        setType(newType);
        if (newType !== "Numeric") setRanges([blankRange()]);
        if (newType !== "Options") setOptions(defaultOpts);
        if (newType === "Options" || newType === "Free Text") setUnit("");
      } catch (e) {
        console.error(e);
      }
    };

    const addOption = () =>
      setOptions((prev) => [...prev, { label: "", isBold: true }]);
    const removeOption = (idx) => {
      if (options.length <= 1) return;
      setOptions((prev) => prev.filter((_, i) => i !== idx));
    };
    const updateOption = (idx, field, value) =>
      setOptions((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: value };
        return next;
      });

    const addRange = () => setRanges((p) => [...p, blankRange()]);
    const removeRange = (index) => {
      if (ranges.length > 1) setRanges((p) => p.filter((_, i) => i !== index));
    };
    const updateRange = (index, field, value) =>
      setRanges((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });

    const resetForm = () => {
      try {
        setName("");
        setType("");
        setUnit("");
        setOrder("");
        setNotes("");
        setMethod("");
        setIsActive(true);
        setOptions(defaultOpts);
        setRanges([blankRange()]);
        setEditingParameter(null);
      } catch (e) {
        console.error(e);
      }
    };
    const { user } = useAuth();

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (!testId) return alert("No test selected.");

        const cleanedOptions =
          type === "Options"
            ? options
              .filter((o) => (o?.label ?? "").trim())
              .filter(
                (o, i, arr) =>
                  arr.findIndex(
                    (x) => x.label.trim() === o.label.trim(),
                  ) === i,
              )
            : [];

        const payload = {
          name,
          type: type || null,
          unit: type === "Options" ? null : unit || null,
          order: order ? parseInt(order, 10) : null,
          notes: notes || null,
          method,
          isActive,
          createdById: user?.id,
          resultOpts:
            type === "Options"
              ? cleanedOptions.map((o) => ({
                label: o.label.trim(),
                value: o.label.trim(),
                isBold: !!o.isBold,
              }))
              : [],
          ranges:
            type === "Numeric"
              ? ranges.map((r) => ({
                lowerLimit:
                  r.lowerLimit !== "" ? parseFloat(r.lowerLimit) : null,
                upperLimit:
                  r.upperLimit !== "" ? parseFloat(r.upperLimit) : null,
                criticalLow:
                  r.criticalLow !== "" ? parseFloat(r.criticalLow) : null,
                criticalHigh:
                  r.criticalHigh !== "" ? parseFloat(r.criticalHigh) : null,
                referenceRange: r.referenceRange || "Any",
                gender: r.gender,
                normalValueHtml: r.normalValueHtml || null,
                specialConditionHtml: r.specialConditionHtml || null,
              }))
              : [],
        };

        if (editingParameter) {
          await axiosInstance.put(
            `/api/parameters/${editingParameter.id}`,
            payload,
          );
        } else {
          await axiosInstance.post(`/api/parameters/${testId}`, payload);
        }

        await Promise.all([fetchParameters(), fetchItems()]);
        resetForm();
        setIsOpen(false);
        alert(editingParameter ? "Parameter updated" : "Parameter added");
      } catch (err) {
        console.error(err);
        alert(err?.response?.data?.error || "Error saving parameter");
      }
    };

    const handleEditParam = (parameter) => {
      try {
        setEditingParameter(parameter);
        setName(parameter.name || "");
        setType(parameter.type || "");
        setUnit(parameter.unit || "");
        setOrder(parameter.order?.toString() || "");
        setNotes(parameter.notes || "");
        setMethod(parameter.method || "");
        setIsActive(!!parameter.isActive);

        if (parameter.type === "Options") {
          const fromApi = Array.isArray(parameter.resultOpts)
            ? parameter.resultOpts
              .filter((o) => o.label || o.value)
              .map((o) => ({
                label: o.label || o.value,
                isBold: o.isBold !== false,
              }))
            : [];
          setOptions(fromApi.length ? fromApi : defaultOpts);
        } else {
          setOptions(defaultOpts);
        }

        if (parameter.type === "Numeric") {
          setRanges(
            parameter.ranges?.length
              ? parameter.ranges.map((r) => ({
                lowerLimit: r.lowerLimit?.toString() || "",
                upperLimit: r.upperLimit?.toString() || "",
                criticalLow: r.criticalLow?.toString() || "",
                criticalHigh: r.criticalHigh?.toString() || "",
                referenceRange: r.referenceRange,
                gender: r.gender || "Both",
                normalValueHtml: r.normalValueHtml || "",
                specialConditionHtml: r.specialConditionHtml || "",
              }))
              : [blankRange()],
          );
        } else {
          setRanges([blankRange()]);
        }

        setIsOpen(true);
        setActiveTab("parameters");
      } catch (e) {
        console.error(e);
      }
    };

    const handleDeleteParam = async (id) => {
      try {
        if (!window.confirm("Delete this parameter?")) return;
        await axiosInstance.delete(`/api/parameters/${id}`);
        await Promise.all([fetchParameters(), fetchItems()]);
        alert("Deleted successfully");
      } catch (err) {
        console.error(err);
        alert("Error deleting parameter");
      }
    };

    // ---------------- UI ----------------
    return (
      <div className="min-h-screen bg-gray-50 font-sans antialiased">
        <Header isMenuOpen={sidebarOpen} />
        <Sidebar isCollapsed={sidebarCollapsed} isOpen={sidebarOpen} />

        <main
          className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
            }`}>
          <div className="p-6 mx-auto">
            <Breadcrumb />

            <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-start gap-4">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-all text-sm">
                    <ArrowLeft className="w-4 h-4" />
                    <span className="font-medium">Back</span>
                  </button>

                  <div>
                    <h1 className="text-xl font-semibold text-gray-900">
                      {testName || `Test Configuration`}
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                      ID: {testId} • Manage parameters and report items
                    </p>
                  </div>
                </div>

                {testId && (
                  <button
                    onClick={() => setIsOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-all shadow-sm"
                    type="button">
                    <Plus className="w-4 h-4" />
                    Add Parameter
                  </button>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="mt-6 border-b border-gray-200">
              <div className="flex gap-6">
                <button
                  onClick={() => setActiveTab("parameters")}
                  className={`pb-3 px-1 text-sm font-medium transition-all ${activeTab === "parameters"
                      ? "text-indigo-600 border-b-2 border-indigo-600"
                      : "text-gray-500 hover:text-gray-700"
                    }`}
                  type="button">
                  Parameters ({testParameters.length})
                </button>

                <button
                  onClick={() => setActiveTab("items")}
                  className={`pb-3 px-1 text-sm font-medium transition-all ${activeTab === "items"
                      ? "text-indigo-600 border-b-2 border-indigo-600"
                      : "text-gray-500 hover:text-gray-700"
                    }`}
                  type="button">
                  Report Items ({items.length})
                </button>
              </div>
            </div>

            {/* PARAMETERS TAB */}
            {activeTab === "parameters" && (
              <div className="mt-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-5 py-3 bg-gray-50/50 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-700">
                      Test Parameters
                    </h3>
                    <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-full">
                      {loading ? "Loading..." : `${testParameters.length}`}
                    </span>
                  </div>

                  {loading ? (
                    <div className="p-8 text-center">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-indigo-200 border-t-indigo-600"></div>
                      <p className="mt-2 text-sm text-gray-500">Loading...</p>
                    </div>
                  ) : testParameters.length === 0 ? (
                    <div className="p-10 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3">
                        <Plus className="w-6 h-6 text-gray-400" />
                      </div>
                      <h3 className="text-base font-medium text-gray-900 mb-1">
                        No parameters yet
                      </h3>
                      <p className="text-sm text-gray-500 mb-4">
                        Add your first parameter
                      </p>
                      <button
                        onClick={() => setIsOpen(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
                        type="button">
                        <Plus className="w-4 h-4" />
                        Add Parameter
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {testParameters.map((param) => (
                        <div
                          key={param.id}
                          className="p-4 hover:bg-gray-50/50 transition-colors group">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-base font-medium text-gray-900">
                                  {param.name}
                                </span>

                                {param.unit && (
                                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                                    {param.unit}
                                  </span>
                                )}

                                <span
                                  className={`text-xs px-2 py-0.5 rounded-full ${param.type === "Numeric"
                                      ? "bg-blue-50 text-blue-700"
                                      : "bg-purple-50 text-purple-700"
                                    }`}>
                                  {param.type}
                                </span>

                                <span
                                  className={`text-xs px-2 py-0.5 rounded-full ${param.isActive
                                      ? "bg-green-50 text-green-700"
                                      : "bg-gray-100 text-gray-600"
                                    }`}>
                                  {param.isActive ? "Active" : "Inactive"}
                                </span>
                              </div>

                              {param.method && (
                                <p className="text-sm text-gray-500">
                                  Method: {param.method}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => handleEditParam(param)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                                title="Edit"
                                type="button">
                                <Edit2 className="w-4 h-4" />
                              </button>

                              <button
                                onClick={() => handleDeleteParam(param.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                                title="Delete"
                                type="button">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ITEMS TAB */}
            {activeTab === "items" && (
              <div className="mt-6">
                <div className="mb-4 flex flex-wrap gap-2 items-center">
                  <button
                    onClick={() => openNewItem(ITEM_TYPES.HEADING)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-100 border border-amber-200"
                    type="button">
                    <Plus className="w-4 h-4" />
                    Heading
                  </button>

                  <button
                    onClick={() => openNewItem(ITEM_TYPES.NOTES)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 text-xs font-medium rounded-lg hover:bg-purple-100 border border-purple-200"
                    type="button">
                    <Plus className="w-4 h-4" />
                    Notes
                  </button>

                  <button
                    onClick={() => openNewItem(ITEM_TYPES.RICH_TEXT)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-lg hover:bg-indigo-100 border border-indigo-200"
                    type="button">
                    <Plus className="w-4 h-4" />
                    Other Text
                  </button>

                  <button
                    onClick={() => openNewItem(ITEM_TYPES.FREE_TEXT)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 text-rose-700 text-xs font-medium rounded-lg hover:bg-rose-100 border border-rose-200"
                    type="button">
                    <Plus className="w-4 h-4" />
                    Free Text
                  </button>

                  <span className="ml-auto text-xs text-gray-500">
                    Drag anywhere to reorder
                  </span>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-5 py-3 bg-gray-50/50 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-700">
                        Report Structure
                      </h3>
                      <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-full">
                        {itemsLoading ? "..." : `${items.length}`}
                      </span>
                    </div>
                  </div>

                  {itemsLoading ? (
                    <div className="p-8 text-center">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-indigo-200 border-t-indigo-600"></div>
                      <p className="mt-2 text-sm text-gray-500">Loading...</p>
                    </div>
                  ) : items.length === 0 ? (
                    <div className="p-10 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3">
                        <Plus className="w-6 h-6 text-gray-400" />
                      </div>
                      <h3 className="text-base font-medium text-gray-900 mb-1">
                        No items yet
                      </h3>
                      <p className="text-sm text-gray-500">
                        Add items above or create parameters
                      </p>
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}>
                      <SortableContext
                        items={items.map((it) => it.id)}
                        strategy={verticalListSortingStrategy}>
                        <div className="divide-y divide-gray-100">
                          {items.map((item) => (
                            <SortableItem
                              key={item.id}
                              item={item}
                              onEditItem={editItem}
                              onDeleteItem={deleteItem}
                              onEditParam={handleEditParam}
                              onDeleteParam={handleDeleteParam}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>

        {/* ✅ REUSABLE ITEM MODAL */}
        <ReportItemModal
          open={itemModalOpen}
          onClose={() => {
            setItemModalOpen(false);
            setEditingItem(null);
          }}
          editingItem={editingItem}
          itemType={itemType}
          itemTitle={itemTitle}
          setItemTitle={setItemTitle}
          itemHtml={itemHtml}
          setItemHtml={setItemHtml}
          itemSortOrder={itemSortOrder}
          setItemSortOrder={setItemSortOrder}
          itemGender={itemGender}
          setItemGender={setItemGender}
          maxPosition={items.length + 1}
          onSave={saveItem}
        />

        {/* PARAMETER MODAL */}
        {isOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-4">
              <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 sticky top-0 bg-white z-10">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-base font-medium text-gray-900">
                      {editingParameter ? "Edit Parameter" : "New Parameter"}
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">
                      Configure parameter details
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      resetForm();
                    }}
                    className="p-1.5 hover:bg-gray-200 rounded"
                    type="button">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="overflow-y-auto max-h-[calc(90vh-100px)]">
                <div className="p-5 space-y-5">
                  <div className="bg-gray-50/50 rounded-lg p-4 border border-gray-200">
                    <h3 className="text-sm font-medium text-gray-800 mb-4 flex items-center gap-1.5">
                      <span className="w-1 h-4 bg-indigo-500 rounded-full"></span>
                      Basic Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Parameter name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Type
                        </label>
                        <select
                          value={type}
                          onChange={(e) => handleTypeChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500">
                          <option value="">Select</option>
                          <option value="Numeric">Numeric</option>
                          <option value="Options">Options</option>
                       
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Order
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={order}
                          onChange={(e) => setOrder(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="1"
                        />
                      </div>

                      {type !== "Options" && type !== "Free Text" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Unit
                          </label>
                          <input
                            type="text"
                            value={unit}
                            onChange={(e) => setUnit(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"

                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Method
                        </label>
                        <input
                          type="text"
                          value={method}
                          onChange={(e) => setMethod(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"

                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Notes
                        </label>
                        <div className="border border-gray-300 rounded-md overflow-hidden">
                          <TinyMCEFullEditor
                            value={notes || ""}
                            onChange={setNotes}
                            height={200}
                            placeholder="Write notes..."
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="active"
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                          className="w-4 h-4 text-indigo-600 rounded"
                        />
                        <label
                          htmlFor="active"
                          className="text-sm font-medium text-gray-700">
                          Active
                        </label>
                      </div>
                    </div>
                  </div>

                  {type === "Options" && (
                    <div className="bg-gray-50/50 rounded-lg p-4 border border-gray-200">
                      <h3 className="text-sm font-medium text-gray-800 mb-4 flex items-center gap-1.5">
                        <span className="w-1 h-4 bg-purple-500 rounded-full"></span>
                        Allowed Values
                      </h3>

                      <div className="space-y-2.5">
                        {options.map((opt, idx) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <input
                              type="text"
                              value={opt.label}
                              onChange={(e) =>
                                updateOption(idx, "label", e.target.value)
                              }
                              placeholder="Value"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                updateOption(idx, "isBold", !opt.isBold)
                              }
                              title={opt.isBold ? "Bold (click to set Normal)" : "Normal (click to set Bold)"}
                              className={`flex items-center justify-center w-9 h-9 rounded-md border text-sm font-bold transition-colors ${opt.isBold
                                  ? "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"
                                  : "bg-white text-gray-400 border-gray-300 hover:bg-gray-50"
                                }`}>
                              <Bold className="w-4 h-4" />
                            </button>
                            {options.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeOption(idx)}
                                className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md border border-red-200">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={addOption}
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 border border-gray-300">
                          <Plus className="w-4 h-4" />
                          Add Option
                        </button>
                      </div>
                    </div>
                  )}

                  {type === "Numeric" && (
                    <div className="bg-gray-50/50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                          <span className="w-1 h-4 bg-emerald-500 rounded-full"></span>
                          Reference Ranges
                        </h3>
                        <button
                          type="button"
                          onClick={addRange}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-md hover:bg-emerald-100 border border-emerald-200">
                          <Plus className="w-4 h-4" />
                          Add
                        </button>
                      </div>

                      <div className="space-y-3">
                        {ranges.map((range, idx) => (
                          <div
                            key={idx}
                            className="bg-white rounded-lg p-4 border border-gray-200 relative">
                            {ranges.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRange(idx)}
                                className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Remove">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Gender
                                </label>
                                <select
                                  value={range.gender}
                                  onChange={(e) =>
                                    updateRange(idx, "gender", e.target.value)
                                  }
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm">
                                  <option value="Both">Both</option>
                                  <option value="Male">Male</option>
                                  <option value="Female">Female</option>
                                </select>
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Low
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={range.lowerLimit}
                                  onChange={(e) =>
                                    updateRange(
                                      idx,
                                      "lowerLimit",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                                  placeholder="0.00"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  High
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={range.upperLimit}
                                  onChange={(e) =>
                                    updateRange(
                                      idx,
                                      "upperLimit",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                                  placeholder="0.00"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Reference Range
                                </label>
                                <SearchableRangeSelect
                                  value={range.referenceRange || "Any"}
                                  onChange={(val) => updateRange(idx, "referenceRange", val)}
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Normal Value
                                </label>
                                <div className="border border-gray-300 rounded-md overflow-hidden">
                                  <TinyMCEFullEditor
                                    value={range.normalValueHtml || ""}
                                    onChange={(val) =>
                                      updateRange(idx, "normalValueHtml", val)
                                    }
                                    height={180}
                                    placeholder="Enter normal value..."
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Special Condition
                                </label>
                                <div className="border border-gray-300 rounded-md overflow-hidden">
                                  <TinyMCEFullEditor
                                    value={range.specialConditionHtml || ""}
                                    onChange={(val) =>
                                      updateRange(
                                        idx,
                                        "specialConditionHtml",
                                        val,
                                      )
                                    }
                                    height={180}
                                    placeholder="Enter special condition..."
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-md">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 flex items-center gap-1.5">
                    <Save className="w-4 h-4" />
                    {editingParameter ? "Update" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  } catch (e) {
    console.error(e);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-base font-medium text-red-600">
            Something went wrong
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Please refresh and try again
          </p>
        </div>
      </div>
    );
  }
}
